import {
  Modal,
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  OutlinedInput,
  InputLabel,
} from '@mui/material';
import { t } from 'i18next';
import { useChangePasswordModal } from './hooks/useChangePasswordModal';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import _ from 'lodash';
import { Controller, Form } from 'react-hook-form';

interface IChangePasswordModalProps {
  onClose: () => void;
  isOpen: boolean;
}

export const ChangePasswordModal = ({
  onClose,
  isOpen,
}: IChangePasswordModalProps) => {
  const { control, isLoading, onSubmit, showPassword, toggleShowPassword } =
    useChangePasswordModal({ onClose });

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 2,
          borderRadius: 2,
          width: '40%',
        }}
      >
        <Form
          control={control}
          onSubmit={e => onSubmit(e.event)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '2rem',
            width: '100%',
            height: 'fit-content',
            borderRadius: 0,
            position: 'relative',
            gap: '1rem',
          }}
        >
          <Controller
            name='old_password'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl sx={{ width: '100%' }} variant='outlined'>
                <InputLabel required size='medium' id={`old_password-label`}>
                  Old password
                </InputLabel>
                <OutlinedInput
                  {...field}
                  type={showPassword ? 'text' : 'password'}
                  label={t(
                    'profile.privacy.changePasswordModal.currentPasswordLabel',
                  )}
                  error={!_.isNil(error)}
                  fullWidth
                  required
                  endAdornment={
                    <InputAdornment position='end'>
                      <IconButton
                        aria-label={
                          showPassword
                            ? 'hide the password'
                            : 'display the password'
                        }
                        onClick={toggleShowPassword}
                        edge='end'
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
                {!!error?.message && (
                  <FormHelperText error id='password-error'>
                    {error.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />

          <Controller
            name='new_password'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl sx={{ width: '100%' }} variant='outlined'>
                <InputLabel required size='medium' id={`new_password-label`}>
                  New password
                </InputLabel>
                <OutlinedInput
                  {...field}
                  type={showPassword ? 'text' : 'password'}
                  label={t(
                    'profile.privacy.changePasswordModal.newPasswordLabel',
                  )}
                  error={!_.isNil(error)}
                  fullWidth
                  required
                  endAdornment={
                    <InputAdornment position='end'>
                      <IconButton
                        aria-label={
                          showPassword
                            ? 'hide the password'
                            : 'display the password'
                        }
                        onClick={toggleShowPassword}
                        edge='end'
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
                {!!error?.message && (
                  <FormHelperText error id='password-error'>
                    {error.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />

          <Controller
            name='confirm_password'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl sx={{ width: '100%' }} variant='outlined'>
                <InputLabel
                  required
                  size='medium'
                  id={`confirm_password-label`}
                >
                  Confirm password
                </InputLabel>
                <OutlinedInput
                  {...field}
                  type={showPassword ? 'text' : 'password'}
                  label={t(
                    'profile.privacy.changePasswordModal.confirmPasswordLabel',
                  )}
                  error={!_.isNil(error)}
                  fullWidth
                  required
                  endAdornment={
                    <InputAdornment position='end'>
                      <IconButton
                        aria-label={
                          showPassword
                            ? 'hide the password'
                            : 'display the password'
                        }
                        onClick={toggleShowPassword}
                        edge='end'
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
                {!!error?.message && (
                  <FormHelperText error id='password-error'>
                    {error.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />

          <Box
            sx={{
              mt: 3,
              display: 'flex',
              width: '100%',
              justifyContent: 'flex-end',
            }}
          >
            <Button onClick={onClose} sx={{ mr: 1 }}>
              {t('general.cancel')}
            </Button>
            <Button
              type='submit'
              variant='contained'
              color='secondary'
              loadingPosition='center'
              loading={isLoading}
              disabled={isLoading}
              sx={{ color: 'white' }}
            >
              {t('general.save')}
            </Button>
          </Box>
        </Form>
      </Box>
    </Modal>
  );
};
